/**
 * Analytics service for tracking user engagement and SEO metrics
 * Can be integrated with Google Analytics, Matomo, or other analytics services
 */

// Initialize analytics (use actual implementation for preferred analytics platform)
const initAnalytics = () => {
  // Google Analytics is now initialized via the script in index.html
  // This function remains for compatibility and can be used for additional setup
  if (typeof window !== "undefined" && window.gtag) {
    console.log("Google Analytics is ready to track events");
  }
};

// Track page views
const trackPageView = (path, title) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "page_view", {
      page_title: title || document.title,
      page_path: path || window.location.pathname,
      send_to: "G-SYXS7E7ZS4",
    });
  }
};

// Track Core Web Vitals (important for SEO)
const trackWebVitals = (metric) => {
  if (typeof window !== "undefined" && window.gtag) {
    const name = metric.name || "metric";
    const id = metric.id || "";
    const value = Math.round(metric.value * 10) / 10;

    window.gtag("event", name, {
      metric_id: id,
      metric_value: value,
      metric_delta: metric.delta,
      metric_rating: metric.rating,
      non_interaction: true,
      event_category: "Web Vitals",
      send_to: "G-SYXS7E7ZS4",
    });
  }
};

// Track user events
const trackEvent = (category, action, label, value) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
      value: value,
      send_to: "G-SYXS7E7ZS4",
    });
  }
};

// Track conversions (important for measuring SEO success)
const trackConversion = (conversionId, value = 0, currency = "USD") => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "conversion", {
      send_to: `G-SYXS7E7ZS4/${conversionId}`,
      value: value,
      currency: currency,
    });
  }
};

// Track search terms (important for SEO keyword research)
const trackSearch = (searchTerm) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "search", {
      search_term: searchTerm,
      send_to: "G-SYXS7E7ZS4",
    });
  }
};

// Track resource downloads (important for educational content analytics)
const trackResourceDownload = (
  resourceName,
  resourceType,
  resourceCategory
) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "download", {
      event_category: "Resources",
      event_label: resourceName,
      resource_type: resourceType,
      resource_category: resourceCategory,
      send_to: "G-SYXS7E7ZS4",
    });
  }
};

// Track admin actions (for monitoring admin panel usage)
const trackAdminAction = (actionType, entityType, entityId) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", actionType, {
      event_category: "Admin",
      event_label: `${entityType} (ID: ${entityId})`,
      entity_type: entityType,
      entity_id: entityId,
      send_to: "G-SYXS7E7ZS4",
    });
  }
};

// Track report generation
const trackReportGeneration = (reportType, reportFilters) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "generate_report", {
      event_category: "Reports",
      event_label: reportType,
      report_type: reportType,
      report_filters: JSON.stringify(reportFilters),
      send_to: "G-SYXS7E7ZS4",
    });
  }
};

// Track user management
const trackUserManagement = (action, userRole, userId) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", action, {
      event_category: "User Management",
      event_label: `${userRole} (ID: ${userId})`,
      user_role: userRole,
      user_id: userId,
      send_to: "G-SYXS7E7ZS4",
    });
  }
};

// Track content management
const trackContentManagement = (action, contentType, contentId, contentTitle) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", action, {
      event_category: "Content Management",
      event_label: contentTitle || `${contentType} (ID: ${contentId})`,
      content_type: contentType,
      content_id: contentId,
      content_title: contentTitle,
      send_to: "G-SYXS7E7ZS4",
    });
  }
};

// Track system settings changes
const trackSettingsChange = (settingCategory, settingName, previousValue, newValue) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "update_setting", {
      event_category: "System Settings",
      event_label: `${settingCategory}: ${settingName}`,
      setting_category: settingCategory,
      setting_name: settingName,
      previous_value: String(previousValue),
      new_value: String(newValue),
      send_to: "G-SYXS7E7ZS4",
    });
  }
};

export const analyticsService = {
  initAnalytics,
  trackPageView,
  trackWebVitals,
  trackEvent,
  trackConversion,
  trackSearch,
  trackResourceDownload,
  trackAdminAction,
  trackReportGeneration,
  trackUserManagement,
  trackContentManagement,
  trackSettingsChange,
};

export default analyticsService;
